import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Database, endBefore, equalTo, limitToLast, list, object, orderByChild, push, query, QueryConstraint, ref, remove, set, update } from "@angular/fire/database";
import { collection, collectionData, Firestore, query as firestoreQuery, QueryConstraint as FirestoreQueryConstraint } from "@angular/fire/firestore";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ErrorModel } from "src/app/models/error.model";
import { LinkModel } from "src/app/models/link.model";
import { GeocodingModel } from "src/app/models/location.model";
import { MarketModel } from "src/app/models/market.model";
import { IMessage } from "src/app/models/message.model";
import { NotificationBacklog, NotificationConfigModel } from "src/app/models/notification.model";
import { OrderModel } from "src/app/models/order.model";
import { IPayment, IPaymentConfig } from "src/app/models/payment.model";
import { ProductModel } from "src/app/models/product.model";
import { ProfileDetailsModel, ProfileModel } from "src/app/models/profile.model";
import { SubscriptionModel } from "src/app/models/subscription.model";
import { TourneeModel } from "src/app/models/tournee.model";
import { UserModel } from "src/app/models/user.model";

// import { GeoFire} from "geofire";
/*
  Generated class for the DataServiceProvider provider.

  See https://angular.io/docs/ts/latest/guide/dependency-injection.html
  for more info on providers and Angular DI.
*/
@Injectable({ providedIn: "root" })
export class DataService {

  private fns: Functions = inject(Functions);
  private db: Database = inject(Database);
  private firestore: Firestore = inject(Firestore);

  user = <UserModel>{};
  cities = <any>{};
  paymentModes = <any>{};
  profile: ProfileModel;
  authority: ProfileModel;
  platform: string;
  geoFire: any;
  dbRef: any;
  hits = new BehaviorSubject([]);

  rolePlural = {
    producer: "producers",
    consumer: "consumers",
    authority: "authorities"
  };

  constructor(public http: HttpClient) {
    /*this.appVersion.getVersionCode().then(version => {
      this.currentAppVersion = version}
    );*/
  }

  init(details) {}

  private getObjectWithKey(path:string, itemId:string, extra?:any){
    const reference = ref(this.db,path);
    const query = object(reference);
    return query.pipe(map(action => { 
      // console.log(action.snapshot.val());
      if(action.snapshot.val()){
        return {...action.snapshot.val(),key: itemId, ...extra }
      }
      else {
        return null
      }
    }));
  }
  private getObject(path:string){
    const reference = ref(this.db,path);
    const query = object(reference);
    return query.pipe(map(action => { return action.snapshot.val() }));
  }
  private getList(path:string){
    const reference = ref(this.db,path);
    const query = list(reference);
    return query.pipe(map(actions => actions.map( action => action.snapshot.val())));
  }
  private getListWithKeys(path:string,constraints: QueryConstraint[]=[], extra?:any){
    const reference = ref(this.db,path);
    const _query = query(reference, ...constraints);
    const _list = list(_query);
    return _list.pipe(map(actions => actions.map( action => {return {...action.snapshot.val(), key: action.snapshot.key,...extra}})));
  }

  private getCollectionListWithKeys(path:string,constraints: FirestoreQueryConstraint[]=[], extra?:any){
    const reference = collection(this.firestore,path);
    const _query = firestoreQuery(reference, ...constraints);
    const _list = collectionData(_query);
    return _list; //.pipe(map(actions => actions.map( action => {return {...action, key: action.snapshot.key,...extra}})));
  }

  private setObject(path:string,input){
    const reference = ref(this.db,path);
    return set(reference,input);
  }
  private pushObject(path:string,input){
    const reference = ref(this.db,path);
    return push(reference,input);
  }
  private updateObject(path:string,input){
    const reference = ref(this.db,path);
    return update(reference,input);
  }
  private removeObject(path:string){
    const reference = ref(this.db,path);
    return remove(reference);
  }
  public callFunction(name:string, input){
    const callable = httpsCallable(this.fns,name);
    return callable(input);
  }

  public getPaymentConfig(uid: string): Observable<IPaymentConfig> {
    return this.getObject("/users/" + uid + "/payments/config");
  }

  public getCities(): Observable<any> {
    return this.getObject("/config/cities");
  }

  public getAppVersion(os: string): Observable<any> {
    return this.getObject("/config/versions/" + os);
  }

  public getGlobalCommunications(type: string): Observable<any[]> {
    return this.getListWithKeys("/communications/" + type + "/global/open");
  }
  public getLocalCommunications(type: string, countryCode: string, state: string, county: string): Observable<any[]> {
    return this.getListWithKeys("/communications/" + type + "/local/" + countryCode + "/" + state + "/" + county + "/open");
  }

  getMarketName(id: string): Observable<any> {
    return this.getObject("/market-templates/" + id + "/details/name/");
  }
  getProducerName(id: string): Observable<any> {
    return this.getObject("/producers/" + id + "/details/shortDetails/name");
  }
  getProducerTourneeName(producerKey: string,tourneeKey:string): Observable<any> {
    return this.getObject("/producers/"+producerKey+"/templates/" + tourneeKey + "/details/name");
  }
  getTourneeName(id:string): Observable<any> {
    return this.getObject("/sale-templates/" + id + "/details/name");
  }
  getPlaceName(countryCode: string,postalCode:string): Observable<any> {
    return this.getObject("/places/" + countryCode + "/postalCode/" + postalCode + "/address/");
  }
  getUserNotifications(id: string): Observable<NotificationConfigModel> {
    return this.getObject("/users/" + id + "/notifications/");
  }

  updateUserNotification(
    uid: string,
    category: string,
    id: string,
    subcategory: string,
    subid: string,
    channel: string,
    action: string,
    status: boolean
  ): Promise<void> {
    return this.setObject("/users/" + uid + "/notifications/" + category + "/" + id + "/" + subcategory + "/" + subid + "/" + action + "/" + channel,status)
  }

  updateUserNotificationAllChannels(
    uid: string,
    category: string,
    id: string,
    subcategory: string,
    subid: string,
    action: string,
    status: boolean
  ): Promise<void> {
    return this.setObject("/users/" + uid + "/notifications/" + category + "/" + id + "/" + subcategory + "/" + subid + "/" + action,{email:status,app:status})
  }

  public getAreaCounters(): Observable<any> {
    return this.getObject("/counters/public/byarea");
  }

  public getCountersAuto(): Observable<any> {
    return this.getObject("/counters-auto");
  }

  public getProductRefs(): Observable<any> {
    return this.getObject("/config/products");
  }
  public getPaymentModes(): Observable<any> {
    return this.getObject("/config/paymentModes");
  }
  public markettRefsByBusiness(type: string): Observable<any> {
    return this.getObject("/config/products/" + type);
  }

  public getOpenTournees(countryCode: string, city: string): Observable<TourneeModel[]> {
    return this.getListWithKeys("/tournees/" + countryCode + "/" + city + "/open");
  }

  public getUser(itemId): Observable<UserModel> {
    return this.getObjectWithKey("/users/" + itemId,itemId,{uid: itemId});
  }

  public getUserDevices(itemId): Observable<any> {
    return this.getObject("/users/" + itemId + "/devices");
  }

  public getMarkets(status): Observable<MarketModel[]> {
    return this.getListWithKeys("/markets");
  }

  public getMarketsByOwner(status, uid): Observable<MarketModel[]> {
    const constraints = [orderByChild("owner/key"),equalTo(uid)];
    return this.getListWithKeys("/markets/",constraints);
  }

  public getMarket(itemId): Observable<MarketModel> {
    return this.getObjectWithKey("/markets/" + itemId,itemId);
  }

  copyMarket(market: MarketModel): Promise<any> {
    return this.callFunction("sale-copyMarket",{ market: market});
  }
  
  public getMarketLight(itemId): Observable<MarketModel> {
    return this.getObjectWithKey("/sales-light/" + itemId,itemId);
  }

  public getMarketTournees(itemId, status): Observable<TourneeModel[]> {
    return this.getListWithKeys("/market-tournees/"+ itemId);
  }

  public getMarketProducerRole(marketId,status, itemId): Observable<string> {
    return this.getObject("/markets/" + marketId+"/producers/"+itemId+"/role");
  }

  public getMarketProducers(marketId,status): Observable<any> {
    return this.getObject("/markets/" + marketId+"/producers/");
  }

  public getMarketApplicationStatus(marketId,status): Observable<string> {
    return this.getObject("/markets/" + marketId + "/applicationStatus");
  }

  public getSale(itemId): Observable<TourneeModel> {
    return this.getObjectWithKey("/sales/" + itemId, itemId);
  }

  public getSaleLight(itemId): Observable<TourneeModel> {
    return this.getObjectWithKey("/sales-light/" + itemId, itemId);
  }

  public getSaleProducts(itemId): Observable<ProductModel[]> {
    return this.getListWithKeys("/sale-products/" + itemId+"/references");
  }

  public getTournees(): Observable<any> {
    return this.getObject("/tournees/");
  }

  public getUserConfigs(itemId): Observable<any> {
    return this.getObject("/users/"+itemId+"/configurations");
  }

  public setUserConfig(userId, config, value): Promise<any> {
    return this.setObject("/users/"+userId+"/configurations/"+config,value);
  }

  public getTournee(itemId): Observable<TourneeModel> {
    return this.getObjectWithKey("/sales/" + itemId,itemId);
  }

  public getMarketTournee(marketId, itemId): Observable<TourneeModel> {
    return this.getObjectWithKey("/market-tournees/"+ marketId + "/" + itemId,itemId);
  }

  public getMarketPrivate(itemId, uid): Observable<MarketModel> {
    if (this.user.subscription.accountType == "authority") {
      return this.getObjectWithKey("/authorities/" + uid + "/markets/" + itemId, itemId,{owner: { key: uid, role: "authority" }})
    } else if (this.user.subscription.accountType == "producer") {
      return this.getObjectWithKey("/producers/" + uid + "/markets/my/" + itemId, itemId,{owner: { key: uid, role: "producer" }})
    }
  }
  public getMarketsPrivate(uid: string): Observable<MarketModel[]> {
    if (this.user.subscription.accountType == "authority") {
      return this.getListWithKeys("/authorities/" + uid + "/markets/",[], {owner: { key: uid, role: "authority" }});
    } else if (this.user.subscription.accountType == "producer") {
      return this.getListWithKeys("/producers/" + uid + "/markets/my",[], {owner: { key: uid, role: "producer" }});
    }
  }

  public getTourneeProducer(itemId: string, uid: string): Observable<TourneeModel> {
    return this.getObjectWithKey("/producers/" + uid + "/tournees/" + itemId,itemId,{producer: { key: uid }});
  }
  
  public getTourneeProducerOrders(itemId: string, uid: string): Observable<OrderModel[]> {
    return this.getListWithKeys("/producers/" + uid + "/tournees/" + itemId + "/orders/",[],{producer:{key:uid}, tournee: { key: itemId, producer:{key:uid}}});
  }

  public getTourneeNotifiedConsumers(itemId: string, uid: string): Observable<any[]> {
    return this.getListWithKeys("/producers/" + uid + "/tournee-consumers-notified/" + itemId);
  }

  public getTourneeNotifiedConsumersObj(itemId: string, uid: string): Observable<any[]> {
    return this.getObject("producers/" + uid + "/tournee-consumers-notified/" + itemId);
  }

  public getTourneeProducerProducts(itemId: string, uid: string): Observable<ProductModel[]> {
    return this.getListWithKeys("/producers/" + uid + "/tournees/" + itemId+"/products/references/",[],{tournee: { key: itemId, producer:{key:uid}}});
  }

  public getTourneeTemplateProducer(itemId: string, uid: string): Observable<TourneeModel> {
    return this.getObjectWithKey("/producers/" + uid + "/templates/" + itemId,itemId,{producer: { key: uid }});
  }

  public getTourneeDetailsProducer(itemId: string, uid: string): Observable<TourneeModel> {
    const reference = ref(this.db,"/producers/" + uid + "/tournees/" + itemId + "/details");
    const query = object(reference);
    return query.pipe(map(action => { return {key: itemId, producer: { key: uid }, details:{...action.snapshot.val() }}}));
  }

  public getTourneeStatusProducer(itemId: string, uid: string): Observable<unknown> {
    return this.getObject("/producers/" + uid + "/tournees/" + itemId + "/status");
  }

  public getTourneeFinancesProducer(itemId: string, uid: string): Observable<TourneeModel> {
    const reference = ref(this.db,"/producers/" + uid + "/tournees/"+itemId+"/finances");
    const query = object(reference);
    return query.pipe(map(action => { return {key: itemId, producer: { key: uid }, finances:{...action.snapshot.val() }}}));
  }

  public getMyTournees(uid: string, limit?, endBeforeDate?): Observable<TourneeModel[]> {
    const constraints = [orderByChild("details/date"),limitToLast(limit||20)];
    if(endBeforeDate) {constraints.push(endBefore(endBeforeDate))};
    return this.getListWithKeys("/producers/" + uid + "/tournees-light/",constraints,{producer: { key: uid }});
  }
  public getMyTemplates(uid: string): Observable<TourneeModel[]> {
    return this.getListWithKeys("/producers/" + uid + "/templates/",[],{producer: { key: uid }});
  }

  public getProducerProducts(uid: string): Observable<ProductModel[]> {
    return this.getListWithKeys("/producers/" + uid + "/products/references/",[],{producer: { key: uid }});
  }

  public getProducerCategories(uid: string): Observable<GeocodingModel[]> {
    return this.getListWithKeys("/producers/" + uid + "/products/categories/");
  }

  public getProducerPlaces(uid: string): Observable<GeocodingModel[]> {
    return this.getListWithKeys("/producers/" + uid + "/places/");
  }

  public getTourneesByProducer(uid: string): Observable<TourneeModel[]> {
    return this.getListWithKeys("/producers-public/" + uid + "/tournees/",[],{producer: { key: uid }});
  }
  public getProductsByProducer(uid: string): Observable<ProductModel[]> {
    return this.getListWithKeys("/producers-public/" + uid + "/products/references/",[],{producer: { key: uid }});
  }
  public getResellersByProducer(uid: string): Observable<ProfileModel[]> {
    return this.getListWithKeys("/producers-public/" + uid + "/resellers/",[],{role:'reseller',owner: { key: uid }});
  }

  getAllMyOrders(uid): Observable<OrderModel[]> {
    return this.getListWithKeys("/consumers/" + uid + "/orders/",[],{consumer: { key: uid }});
  }
  getOrder(itemId, profileId): Observable<OrderModel> {
    return this.getObjectWithKey("consumers/" + profileId + "/orders/" + itemId,itemId,{consumer: { key: profileId }});
  }

  getProducerOrder(itemId, profileId, tourneeId): Observable<OrderModel> {
    return this.getObjectWithKey("producers/" + profileId + "/tournees/" + tourneeId + "/orders/" + itemId,itemId,{tournee: { key: tourneeId }, producer: { key: profileId }});
  }

  getProducerTourneeProductsOrderedObj(profileId, tourneeId): Observable<any> {
    return this.getObject("producers/" + profileId + "/tournee-orders-products/" + tourneeId);
  }

  getProducerTourneeProductsOrdered(profileId, tourneeId): Observable<OrderModel[]> {
    const reference = ref(this.db,"producers/" + profileId + "/tournee-orders-products/" + tourneeId);
    const query = list(reference);
    return query.pipe(map(actions => actions.map( action => {return {key: action.snapshot.key,products:{references:{...action.snapshot.val()}}}})));
  }
  getProducerOrderProductsOrdered(profileId, tourneeId, orderId): Observable<ProductModel[]> {
    return this.getListWithKeys("producers/" + profileId + "/tournee-orders-products/" + tourneeId+'/'+orderId,[],{producer: { key: profileId }, order:{key:orderId, tournee:{key:tourneeId}}});
  }

  getProducerOrderProductsOrderedObj(profileId, tourneeId, orderId): Observable<any> {
    return this.getObject("producers/" + profileId + "/tournee-orders-products/" + tourneeId+'/'+orderId);
  }

  getProducerConsumerOrders(itemId, profileId): Observable<OrderModel[]> {
    return this.getListWithKeys("producers/" + profileId + "/consumers/" + itemId + "/orders/",[],{producer: { key: profileId }})
  }

  getProducerSaleDetails(itemId, profileId): Observable<TourneeModel> {
    return this.getObjectWithKey("producers/" + profileId + "/tournees/" + itemId + "/details/",itemId,{producer: { key: profileId }});
  }

  getMarketOrders(tourneeId, profileId): Observable<OrderModel[]> {
    return this.getListWithKeys("producers/" + profileId + "/tournees/" + tourneeId + "/orders/",[],{tournee: { key: tourneeId, producer: { key: profileId } }})
  }

  getProducer(itemId): Observable<ProfileModel> {
    return this.getObjectWithKey("/producers/" + itemId,itemId);
  }

  getLink(link1, link2?, link3?): Observable<LinkModel> {
    let url = "/links/" + link1;
    // url += link2 ? "/" + link2 : "";
    // url += link3 ? "/" + link3 : "";
    console.log(url);
    return this.getObject(url);
  }

  getProducerProduct(itemId, uid): Observable<ProductModel> {
    return this.getObjectWithKey("/producers/" + uid + "/products/references/" + itemId, itemId,{producer: { key: uid }});
  }

  getTourneeProduct(itemId, tourneeId, uid): Observable<ProductModel> {
    return this.getObjectWithKey("/producers/" + uid + "/tournees/"+tourneeId+"/products/references/" + itemId, itemId,{tournee:{key:tourneeId}, producer: { key: uid }});
  }

  getProducerReseller(itemId, uid): Observable<ProfileModel> {
    return this.getObjectWithKey("/producers/" + uid + "/resellers/" + itemId, itemId,{owner: { key: uid }});
  }

  getAuthority(itemId): Observable<ProfileModel> {
    return this.getObjectWithKey("/authorities/" + itemId, itemId);
  }

  getBasket(itemId): Observable<OrderModel> {
    return this.getObjectWithKey("/baskets/" + itemId, itemId);
  }

  getProducerDetails(itemId): Observable<ProfileDetailsModel> {
    return this.getObject("/producers/" + itemId + "/details");
  }

  searchProducersByName(name): Observable<ProfileModel[]> {
    const constraints = [orderByChild("details/shortDetails/name"), equalTo(name),limitToLast(20)];
    return this.getListWithKeys("/producers-public/",constraints);
  }

  searchProducersByEmail(name): Observable<ProfileModel[]> {
    const constraints = [orderByChild("details/fullDetails/email"), equalTo(name),limitToLast(20)];
    return this.getListWithKeys("/producers-public/",constraints);
  }

  getProducerPrivate(itemId): Observable<any> {
    return this.getObject("/producers/" + itemId + "/private");
  }

  getAuthorityDetails(itemId): Observable<ProfileDetailsModel> {
    return this.getObject("/authorities/" + itemId + "/details");
  }

  getProfileDetails(itemId,type): Observable<ProfileDetailsModel> {
    const types = {
      consumer:'consumers',producer:'producers',authority:'authorities'
    }
    return this.getObject("/"+types[type]+"/" + itemId + "/details");
  }
  
  getMyProducts(itemId): Observable<ProductModel[]> {
    return this.getListWithKeys("/producers/" + itemId + "/products/references",[],{producer: { key: itemId }});
  }

  getProductStats(producerId, itemId, from, to): Observable<ProductModel[]> {
    return this.getCollectionListWithKeys("/producers/" + producerId + "/products/"+itemId+"/stats",[],{producer: { key: itemId }});
  }

  getMyConsumers(itemId: string): Observable<ProfileModel[]> {
    return this.getListWithKeys("/producers/" + itemId + "/consumers");
  }

  getMyUnregisteredConsumers(itemId: string): Observable<ProfileModel[]> {
    return this.getListWithKeys("/producers/" + itemId + "/consumers",[orderByChild("status"),equalTo("unregistered")])
  }
  
  getMyProducers(uid: string): Observable<ProfileModel[]> {
    return this.getListWithKeys("/users/" + uid + "/producers",[],{owner: { key: uid }, role:'producer',type:'producer'});
  }

  getMyAdministrators(uid: string, role:string): Observable<ProfileModel[]> {
    return this.getListWithKeys("/"+this.rolePlural[role]+"/" + uid + "/users");
  }

  getMyProducer(itemId: string, ownerId:string): Observable<ProfileModel> {
    return this.getObjectWithKey("/users/" + ownerId + "/producers/" + itemId,itemId,{role:'admin',owner: { key: ownerId },type:'producer'});
  }

  getMyResellers(itemId: string): Observable<ProfileModel[]> {
    return this.getListWithKeys("/producers/" + itemId + "/resellers",[],{owner: { key: itemId }, role:'admin',type:'reseller'})
  }
  
  getConsumer(itemId: string): Observable<ProfileModel> {
    return this.getObjectWithKey("/consumers/" + itemId,itemId);
  }

  getConsumerDetails(itemId: string): Observable<ProfileDetailsModel> {
    return this.getObject("/consumers/" + itemId + "/details");
  }

  getConsumerPrivate(itemId: string): Observable<any> {
    return this.getObject("/consumers/" + itemId + "/private");
  }

  getConsumerPlaces(itemId: string): Observable<GeocodingModel[]> {
    return this.getListWithKeys("/consumers/" + itemId + "/places");
  }

  createConsumerPlaces(consumerId:string, item:GeocodingModel) {
    return this.pushObject("/consumers/" + consumerId + "/places",item);
  }

  createNotification(item:NotificationBacklog) {
    return this.pushObject("/notifications-backlog/todo",item);
  }

  createProducerPlaces(consumerId:string, item:GeocodingModel) {
    return this.pushObject("/producers/" + consumerId + "/places",item);
  }


  getMessage(item: any, tourneeKey: string, orderKey: string) {
    return this.getListWithKeys("/messages/sales/" + tourneeKey + "/orders/" + orderKey);
  }

  getPayment(paymentKey): Observable<IPayment> {
    return this.getObjectWithKey("/payments/"+paymentKey,paymentKey)
  }

  createProfile(item: ProfileModel, role) {
    return this.setObject("/" + this.rolePlural[role] + "/" + item.key,item);
  }

  createProducer(item: ProfileModel) {
    return this.setObject("/producers/" + item.key,item);
  }

  createAuthority(item: ProfileModel) {
    return this.setObject("/authorities/" + item.key,item);
  }

  createReseller(item: ProfileModel) {
    return this.setObject("/resellers/" + item.key,item);
  }

  createBasket(item: OrderModel) {
    return this.pushObject("/baskets/",item);
  }

  createConsumer(item: ProfileModel) {
    return this.setObject("/consumers/" + item.key,item);
  }

  createTournee(item: TourneeModel) {
    return this.pushObject("/producers/" + item.producer.key + "/tournees",item);
  }

  createAdministrator(uid:string,item:ProfileModel){
    return this.pushObject("/producers/" + uid + "/users",item);
  }

  createProduct(item: ProductModel) {
    return this.pushObject("/producers/" + item.producer.key + "/products/references",item);
  }
  createProducerReseller(item: ProfileModel) {
    return this.pushObject("/producers/" + item.owner.key + "/resellers",item);
  }

  createManagedProducer(item: ProfileModel) {
    return this.pushObject("/users/" + item.owner.key + "/producers",item);
  }

  createMarket(item: MarketModel) {
    return this.pushObject("/markets",item);
  }

  createOrderPayment(order:OrderModel, item:IPayment){
    return this.pushObject("/producers/"+ order.tournee.producer.key + "/tournees/"+ order.tournee.key + "/orders/" + order.key + "/payments",item);
  }

  createMarketPayment(order:OrderModel, item:IPayment){
    return this.pushObject("/markets-private/"+ order.market.key + "/payments/",item);
  }

  logError(module, context, code, message, data?, email?) {
    const dateTime = new Date().toISOString();
    const date = dateTime.slice(0, 10);
    const error: ErrorModel = {
      dateTime: dateTime,
      uid: this.user.uid || "0",
      email: email || this.user.email,
      role: this.user.role || "anonymous",
      url: window.location.href,
      context: context,
      module: module,
      message: message,
      code: code,
      data: data
    };
    console.log(error);
    return this.pushObject("/errors/" + error.module + "/" + date,error);
  }

  addProducerToMarket(item: MarketModel, producer: ProfileModel) {
    if (item.details.autoAccept == true) {
      return this.setObject("/markets/" + item.key + "/producers/" + producer.key,producer)
    }
  }

  createOrder(item: OrderModel) {
    return this.pushObject("/consumers/" + item.consumer.key + "/orders",item);
  }

  addOrderIntoProducer(item: OrderModel, orderKey:string, producerKey:string, tourneeKey:string) {
    return this.setObject("/producers/" + producerKey + "/tournees/"+ tourneeKey+ "/orders/"+orderKey,item);
  }

  createOrderIntoProducer(item: OrderModel, producerKey:string, tourneeKey:string) {
    return this.pushObject("/producers/" + producerKey + "/tournees/"+ tourneeKey+ "/orders/",item);
  }

  createProducerConsumer(item: ProfileModel, producerKey:string) {
    return this.pushObject("/producers/" + producerKey + "/consumers",item);
  }

  createMessage(item: IMessage) {
    let url = '/messages/'+item.type+'s/'+item.itemKey;
    if(item.recipientType == 'individual' && item.orderKey){
      url+= "/orders/" + item.orderKey;
    }
    else{
      url +=  "/public";
    }
    return this.pushObject(url,item);
  }

  createGlobalCommunication(type: string, message: string) {
    return this.pushObject("/communications/" + type + "/global/open",message);
  }

  createLocalCommunication(type: string, countryCode: string, state: string, county: string, message: string) {
    return this.pushObject("/communications/" + type + "/local/" + countryCode + "/" + state + "/" + county + "/open",message);
  }

  createProductRef(item: ProductModel) {
    return this.pushObject("/config/products/" + item.details.type,item);
  }
  createPaymentMode(paymentMode: any) {
    return this.pushObject("/config/paymentModes",paymentMode);
  }

  createCity(city: GeocodingModel) {
    return this.setObject("/config/cities/" + city.address.countryCode + "/" + city.address.postalCode,city);
  }

  createProductOrder(order: OrderModel, product: ProductModel) {
    return this.pushObject("/consumers/" + order.consumer.key + "/orders/" + order.key + "/products/references",product);
  }

  updateOrderPayment(order:OrderModel, item:IPayment){
    return this.updateObject("/producers/"+ order.tournee.producer.key + "/tournees/"+ order.tournee.key + "/orders/" + order.key + "/payments/"+item.key,item);
  }

  setPaymentConfig(config,uid:string) {
    return this.setObject("/users/" + uid + "/payments/config",config);
  }

  deleteAuthority(item: ProfileModel) {
    return this.removeObject("/authorities/" + item.key);
  }
  deleteProducer(item: ProfileModel) {
    return this.removeObject("/producers/" + item.key);
  }
  deleteTournee(item: TourneeModel) {
    return this.removeObject("/producers/" + item.producer.key+ '/tournees/'+item.key);
  }
  deleteConsumer(item: ProfileModel) {
    return this.removeObject("/consumers/" + item.key);
  }

  deleteProduct(item: ProductModel) {
    return this.removeObject("/producers/" + item.producer.key + "/products/references/" + item.key);
  }

  deletePayment(item: IPayment, order: OrderModel) {
    return this.removeObject("/producers/" + order.tournee.producer.key + "/tournees/"+order.tournee.key + "/orders/"+ order.key + "/payments/" + item.key);
  }

  deleteAdministrator(uid:string, item: ProfileModel) {
    return this.removeObject("/producers/" + uid + "/users/" + item.key);
  }

  deleteProducerReseller(item: ProfileModel) {
    return this.removeObject("/producers/" + item.owner.key + "/resellers/" + item.key);
  }

  deleteMarket(item: MarketModel, uid) {
    return this.removeObject("/markets/" + item.key);
  }

  deleteProductRef(product: ProductModel): Promise<void> {
    return this.removeObject("/configs/products/" + product.details.type + "/" + product.key);
  }
  deletePaymentMode(paymentMode: any): Promise<void> {
    return this.removeObject("/configs/paymentmodes/" + paymentMode.key);
  }
  deleteCity(city: GeocodingModel): Promise<void> {
    return this.removeObject("/configs/cities/" + city.address.countryCode + "/" + city.address.postalCode);
  }
  updateAuthorityNotification(item: ProfileModel, type: string, channel: string, status: boolean): Promise<void> {
    return this.updateObject("/authorities/" + item.key + "/private/notifications/" + type,{ [channel]: status });
  }
  updateConsumerNotification(consumer: ProfileModel, type: string, channel: string, status: boolean): Promise<void> {
    return this.updateObject("/consumers/" + consumer.key + "/private/notifications/" + type,{ [channel]: status });
  }
  updateProducerNotification(producer: ProfileModel, type: string, channel: string, status: boolean): Promise<void> {
    return this.updateObject("/producers/" + producer.key + "/private/notifications/" + type,{ [channel]: status });
  }

  updateProductRef(item: ProductModel): Promise<void> {
    return this.updateObject("/configs/products/" + item.details.type + "/" + item.key,item);
  }
  updatePaymentMode(item: any): Promise<void> {
    return this.updateObject("/configs/paymentModes/" + item.key,item);
  }

  updateMessageStatus(item: any, tourneeKey: string, orderKey: string): Promise<void> {
    return this.updateObject("/messages/tournees/" + tourneeKey + "/orders/" + orderKey + "/" + item.key + "/status",item.status);
  }

  updateCity(item: GeocodingModel): Promise<void> {
    return this.updateObject("/configs/cities/" + item.address.countryCode + "/" + item.address.postalCode,item);
  }

  updateBasket(item: OrderModel): Promise<void> {
    return this.updateObject("/baskets/" + item.key,item);
  }
  updateBasketStatus(item: OrderModel): Promise<void> {
    const refObject = ref(this.db,);
    return this.updateObject("/baskets/" + item.key,{status:item.status});
  }

  updateMarket(item: MarketModel): Promise<void> {
    return this.updateObject("/markets/" + item.key,{ details: item.details, 'notifications/config':item.notifications.config  });
  }
  updateMarketStatus(item: MarketModel): Promise<void> {
    return this.updateObject("/markets/" + item.key,{ status: item.status });
  }
  updateMarketApplicationStatus(item: MarketModel, status: string): Promise<void> {
    return this.updateObject("/markets/" + item.key,{ applicationStatus: status });
  }
  updateMarketProducerRole(item: MarketModel, producer: ProfileModel, role: string): Promise<void> {
    return this.updateObject("/markets/" + item.key + "/producers/" + producer.key,{ role: role });
  }
  updateApplicationStatusByOwner(market: MarketModel, producer: ProfileModel): Promise<void> {
    return this.updateObject("/markets/" + market.key + "/producers/" + producer.key,{ marketApplicationStatus: producer.marketApplicationStatus });
  }

  updateTournee(item: TourneeModel): Promise<void> {
    return this.updateObject("/producers/" + item.producer.key + "/tournees/" + item.key,{ details: item.details, 'notifications/config':item.notifications.config });
  }
  updateProduct(item: ProductModel): Promise<void> {
    return this.updateObject("/producers/" + item.producer.key + "/products/references/" + item.key,{ details: item.details });
  }

  updateProductRank(item: ProductModel): Promise<void> {
    return this.updateObject("/producers/" + item.producer.key + "/products/references/" + item.key + "/details/",{ rank: item.details.rank });
  }

  updateTourneeProduct(item: TourneeModel, product: ProductModel): Promise<void> {
    return this.updateObject("/producers/" + item.producer.key + "/tournees/" + item.key + "/products/references/" + product.key,{ details: product.details, status: product.status || null, 
      stockStatus: product.stockStatus || null, 
      stock:product.stock || null,
      icons:product.icons || null});
  }

  updateTourneeProductDetails(item: TourneeModel, product: ProductModel): Promise<void> {
    return this.updateObject("/producers/" + item.producer.key + "/tournees/" + item.key + "/products/references/" + product.key,{ details: product.details});
  }

  updateTourneeProductStock(item: TourneeModel, product: ProductModel): Promise<void> {
    return this.updateObject("/producers/" + item.producer.key + "/tournees/" + item.key + "/products/references/" + product.key,{ 
      stock: product.stock, stockStatus: product.stockStatus });
  }

  updateProducerReseller(item: ProfileModel): Promise<void> {
    return this.updateObject("/producers/" + item.owner.key + "/resellers/" + item.key,{ details: item.details });
  }
  updateProductStatus(item: ProductModel): Promise<void> {
    return this.updateObject("/producers/" + item.producer.key + "/products/references/" + item.key,{ status: item.status });
  }
  updateProducerResellerStatus(item: ProfileModel): Promise<void> {
    return this.updateObject("/producers/" + item.owner.key + "/resellers/" + item.key,{ status: item.status });
  }
  updateTourneeStatus(item: TourneeModel): Promise<void> {
    return this.updateObject("/producers/" + item.producer.key + "/tournees/" + item.key,{ status: item.status });
  }

  updateTourneeNextDate(item: TourneeModel): Promise<void> {
    return this.updateObject("/producers/" + item.producer.key + "/tournees/" + item.key+"/details",{ nextDate: item.details.nextDate });
  }

  updateTourneeRoute(item: TourneeModel, producer: ProfileModel, route: any): Promise<void> {
    return this.setObject("/producers/" + producer.key + "/tournees/" + item.key + "/route",route);
  }

  updateOrderProducts(item: OrderModel): Promise<void> {
    return this.updateObject("/consumers/" + item.consumer.key + "/orders/" + item.key + "/products",item.products);
  }

  updateProducerOrderProduct(item: OrderModel, product:ProductModel): Promise<void> {
    console.log(item,product);
    return this.updateObject("/producers/" + item.tournee.producer.key + "/tournee-orders-products/"+ item.tournee.key +"/" + item.key + "/"+product.key,product);
  }

  updateOrderStatusByConsumer(item: OrderModel): Promise<void> {
    const refObject = ref(this.db,);
    return this.updateObject("/consumers/" + item.consumer.key + "/orders/" + item.key,{ status: item.status });
  }

  updateOrderPaymentStatusByConsumer(item: OrderModel, consumer: ProfileModel): Promise<void> {
    const refObject = ref(this.db,);
    return this.updateObject("/consumers/" + consumer.key + "/orders/" + item.key,{ paymentStatus: item.paymentStatus })
  }

  updateOrderTotalSoldByProducer(item: OrderModel, producer: ProfileModel): Promise<void> {
    return this.updateObject("/producers/" + producer.key + "/tournees/" + item.tournee.key + "/orders/" + item.key + "/finances/totals",item.finances.totals)
  }

  updateOrderStatusByProducer(item: OrderModel, producer: ProfileModel): Promise<void> {
    return this.updateObject("/producers/" + producer.key + "/tournees/" + item.tournee.key + "/orders/" + item.key,{ status: item.status })
  }

  updateOrderPaymentStatusByProducer(item: OrderModel, producer: ProfileModel): Promise<void> {
    return this.updateObject("/producers/" + producer.key + "/tournees/" + item.tournee.key + "/orders/" + item.key,{ paymentStatus: item.paymentStatus, 'finances/totals/paid':item.finances.totals.paid })
  }
  updateOrderRejectedCommentByProducer(item: OrderModel, producer: ProfileModel): Promise<void> {
    return this.updateObject("/producers/" + producer.key + "/tournees/" + item.tournee.key + "/orders/" + item.key + "/details",{ commentRejected: item.details.commentRejected });
  }
  updateOrderPreparedCommentByProducer(item: OrderModel, producer: ProfileModel): Promise<void> {
    return this.updateObject("/producers/" + producer.key + "/tournees/" + item.tournee.key + "/orders/" + item.key + "/details",{ commentPrepared: item.details.commentPrepared||null, secret:item.details.secret||null, locker:item.details.locker||null })
  }

  updateAuthority(item: ProfileModel): Promise<void> {
    return this.updateObject("/authorities/" + item.key,{ details: item.details });
  }
  updateConsumer(item: ProfileModel): Promise<void> {
    return this.updateObject("/consumers/" + item.key,{ details: item.details })
  }
  updateConsumerFullDetails(item: ProfileModel): Promise<void> {
    return this.updateObject("/consumers/" + item.key + "/details",{ fullDetails: item.details.fullDetails, shortDetails: item.details.shortDetails, fullProfile: item.details.fullProfile })
  }
  updateConsumerGeocoding(item: ProfileModel): Promise<void> {
    const refObject = ref(this.db,);
    return this.updateObject("/consumers/" + item.key + "/details",{ geocoding: item.details.geocoding })
  }
  updateProducerConsumerGeocoding(item: ProfileModel, producerKey:string): Promise<void> {
    return this.updateObject("/producers/" + producerKey + "/consumers/" + item.key + "/details",{ geocoding: item.details.geocoding })
  }
  updateProducerConsumerDetails(item: ProfileModel, producerKey:string): Promise<void> {
    return this.updateObject("/producers/" + producerKey + "/consumers/" + item.key + "/details",{ fullDetails: item.details.fullDetails, shortDetails: item.details.shortDetails, fullProfile: item.details.fullProfile })
  }
  updateProducer(item: ProfileModel): Promise<void> {
    return this.updateObject("/producers/" + item.key,{ details: item.details })
  }
  updateProfilePhoto(item: ProfileModel, URL: string, role: string, photoTimeStamp:string): Promise<void> {
    return this.updateObject("/" + this.rolePlural[role] + "/" + item.key + "/details/shortDetails",{ photoURL: URL, photoTimeStamp:photoTimeStamp })
  }
  updateMarketPhoto(item: MarketModel, URL: string, photoTimeStamp:string): Promise<void> {
    return this.updateObject("/markets/" + item.key + "/details",{ photoURL: URL, photoTimeStamp:photoTimeStamp })
  }
  updateTourneePhoto(item: TourneeModel, URL: string, photoTimeStamp:string): Promise<void> {
    return this.updateObject("/producers/" + item.producer.key + "/tournees/" + item.key + "/details",{ photoURL: URL, photoTimeStamp:photoTimeStamp });
  }
  updateProductPhoto(item: ProductModel, URL: string, photoTimeStamp:string): Promise<void> {
    return this.updateObject("/producers/" + item.producer.key + "/products/references/" + item.key + "/details",{ photoURL: URL, photoTimeStamp:photoTimeStamp });
  }
  updateUserSubscription(user: UserModel, subscription: SubscriptionModel): Promise<void> {
    return this.updateObject("/users/" + user.uid,{ subscription: subscription });
  }
  setUserToken(user: UserModel, token: string, uuid:string, device: any): Promise<void> {
    return this.setObject("/users/" + user.uid + "/devices/"+uuid,{ 
      token:token, 
      dateTime:new Date().toISOString(),
      device: device });
  }
  ////////////////////////////////////////////:
  //////// ADMIN /////////

  getNotifications(): Observable<any> {
    return this.getObject('/notifications');
  }

  getUsers(): Observable<UserModel[]> {
    return this.getListWithKeys("users");
  }

  getConsumers(): Observable<ProfileModel[]> {
    return this.getListWithKeys("consumers");

  }
  getProducers(): Observable<ProfileModel[]> {
    return this.getListWithKeys("producers");
  }

  renewMarket(key:string, nextDate:string): Promise<any> {
    return this.callFunction("sale-renewMarket",{key:key, details:{nextDate:nextDate}});
  }
  makeMarketPublic(key:string): Promise<any> {
    return this.callFunction("sale-makeMarketPublic",{key:key});
  }

  makeSalePublic(key:string, producerKey:string): Promise<any> {
    return this.callFunction("sale-makeSalePublic",{key:key, producerKey: producerKey});
  }

  transformIntoMarket(tournee:TourneeModel): Promise<any> {
    return this.callFunction("migration-transformIntoMarket",{key:tournee.key, producer:{key:tournee.producer.key}});
  }

  calculateProductStat(uid:string){
    return this.callFunction("stats-calculateProductStat",{producer:{key:uid}});
  }
}
